/** Where a bow is pointed: what this client sends, and what it draws for one.
 *
 *  The original's aim is a `CFlyTarget` on the actor, which is either an object
 *  or a position and answers where it is *now* (`FlyTarget.cpp:4-120`,
 *  `ActorInstanceFly.cpp:4-116`). Here a flight resolves its target by entity
 *  id each step (`render/arrowflight.ts`), so the aim is not stored between
 *  frames; these are the two moments it is needed -- the shot that sends one
 *  and the frame that draws somebody else's.
 */

import { ARROW_HEIGHT_MM } from "../render/arrowflight";

/** Where a shot is going: an entity, or a fixed point when `target` is zero. */
export interface Aim {
  readonly shooter: number;
  readonly target: number;
  readonly xMm: number;
  readonly yMm: number;
}

export interface AimPoint {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
}

export interface Quarry {
  readonly id: number;
  readonly xMm: number;
  readonly yMm: number;
}

/** What this shot is aimed at, or null when the swing sends nothing.
 *
 *  `OnSetFlyTarget` reads the target instance and its current fly position and
 *  sends both (`PythonPlayerEventHandler.cpp:232-240`), so a shot with no
 *  target instance sends no aim at all -- the bare-point case belongs to the
 *  splash skills, which push their own points (`PythonPlayerSkill.cpp:736-743`).
 *  A swing that is not ranged is not an aim either: the packet leaves the bow
 *  animation's fly event and nothing else raises one.
 */
export function aimForSwing(quarry: Quarry | null, ranged: boolean): Aim | null {
  if (!ranged || quarry === null) return null;
  return { shooter: 0, target: quarry.id, xMm: quarry.xMm, yMm: quarry.yMm };
}

/** The two ends of the arrow an aim at a bare point draws, or null.
 *
 *  Only the bare point. An aim at an entity is drawn by the blow that lands on
 *  it -- `arrowShotFor` in `render/entities.ts` -- and drawing it twice would
 *  put two arrows in the air for one shot. A shot at nobody produces no blow
 *  and so no arrow at all, which is the case `RecvFlyTargetingPacket`'s own
 *  else-branch exists for (`PythonNetworkStreamPhaseGame.cpp:2569-2574`).
 */
export function groundAimFlight(
  aim: Aim,
  positionOf: (entityId: number) => { xMm: number; yMm: number } | null,
  groundAt: (xMm: number, yMm: number) => number,
): { from: AimPoint; to: AimPoint } | null {
  if (aim.target !== 0) return null;
  const shooter = positionOf(aim.shooter);
  if (shooter === null) return null;
  return {
    from: {
      xMm: shooter.xMm,
      yMm: shooter.yMm,
      zMm: groundAt(shooter.xMm, shooter.yMm) + ARROW_HEIGHT_MM,
    },
    to: {
      xMm: aim.xMm,
      yMm: aim.yMm,
      zMm: groundAt(aim.xMm, aim.yMm) + ARROW_HEIGHT_MM,
    },
  };
}
